import React from "react";
import { PanelHelp } from "./QuickStart";
import { toast } from "sonner";
import { Archive } from "lucide-react";
import { StrikeChip, StrikeHint, useTicketStrikes } from "./StrikeChip";
import { EditableTargetBadge } from "./EditableTargetBadge";
import {
  buildExoticGroups,
  countExactaCombos,
  EXACTA_MIN_BASE,
  TRIFECTA_MIN_BASE,
  TRIFECTA_KB_SIZES,
  TRIFECTA_KB_DEFAULT_SIZE,
} from "../lib/exoticsHelpers";
import { saveTicket } from "../lib/ticketHistory";

const triSlot = (line, g) => `line${line}g${g}`;
// Pairs (a, b) with a in A, b in B, a !== b.
const countPairs = (a, b) => a.length * b.length - a.filter((n) => b.includes(n)).length;

const fmtCost = (n) => {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? rounded.toFixed(2) : rounded.toFixed(2);
};

// Deprecated: TargetBadge is now the shared EditableTargetBadge component.

const BaseSelector = ({ value, onChange, options, testIdPrefix }) => (
  <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-base-selector`}>
    <span
      className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mr-1"
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
    >
      Base
    </span>
    {options.map((v) => {
      const active = value === v;
      return (
        <button
          key={v}
          data-testid={`${testIdPrefix}-base-${v}`}
          onClick={() => onChange(v)}
          className={`px-3 h-8 rounded-md text-[11px] font-bold tracking-[0.14em] uppercase transition-colors border ${
            active
              ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
              : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
          }`}
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          ${v.toFixed(2)}
        </button>
      );
    })}
  </div>
);

const Line = ({ label, sublabel, horses, tone, prefix, strikes, slot }) => {
  const styles = {
    lime: { bg: "#84cc16", accent: "#0a0a0a" },
    cream: { bg: "#f5ecd6", accent: "#0a0a0a" },
    sand: { bg: "#e7d7a8", accent: "#0a0a0a" },
  };
  const s = styles[tone];
  return (
    <div
      data-testid={`line-${prefix}`}
      className="flex items-center gap-4 px-5 py-4 rounded-md"
      style={{ background: s.bg }}
    >
      <div className="min-w-[110px]">
        <div
          className="text-[11px] uppercase tracking-[0.22em] font-bold"
          style={{ color: s.accent, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          {label}
        </div>
        <div
          className="text-[10px] uppercase tracking-[0.16em] mt-0.5"
          style={{ color: s.accent, opacity: 0.65, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          {sublabel}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {horses.map((n, i) => (
          <StrikeChip
            key={`${n}-${i}`}
            n={n}
            testId={`${prefix}-${i}`}
            struck={strikes.isStruck(slot, n)}
            onToggle={() => strikes.toggle(slot, n)}
          />
        ))}
      </div>
    </div>
  );
};

const Divider = ({ label, testId }) => (
  <div
    data-testid={testId}
    className="text-center text-stone-500 text-[11px] uppercase tracking-[0.4em] py-1"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
  >
    {label}
  </div>
);

// Renders a single Trifecta Key-Box permutation line, e.g.
//   KEY 1st   [11]  with  [2 4 5 3 6]  with  [2 4 5 3 6]
// The key chip is highlighted so the structure is unmistakable.
const KeyBoxLine = ({ position, keyPost, withPosts, prefix, strikes, combos }) => {
  const groups = [1, 2, 3].map((g) => (g === position ? [keyPost] : withPosts));
  const posLabel = ["", "1st", "2nd", "3rd"][position];
  return (
    <div
      data-testid={`line-${prefix}`}
      className="px-5 py-3 rounded-md"
      style={{ background: "#f5ecd6" }}
    >
      <div
        className="flex items-baseline gap-3 mb-2 text-[#0a0a0a]"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      >
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold">Key {posLabel}</span>
        <span className="text-[10px] uppercase tracking-[0.16em]" style={{ opacity: 0.6 }}>
          #{keyPost} in {posLabel}
        </span>
        <span
          data-testid={`${prefix}-combos`}
          className="ml-auto text-[10px] uppercase tracking-[0.16em] font-bold"
          style={{ opacity: 0.8 }}
        >
          {combos} combo{combos === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
      {groups.map((grp, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && (
            <span
              className="text-[10px] uppercase tracking-[0.3em] font-bold px-1 text-[#0a0a0a]"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", opacity: 0.5 }}
            >
              with
            </span>
          )}
          <div
            data-testid={`${prefix}-g${gi + 1}`}
            className={`flex items-center gap-1.5 flex-wrap ${
              gi + 1 === position
                ? "ring-2 ring-[#84cc16] rounded-md px-1.5 py-1"
                : ""
            }`}
          >
            {grp.map((n, i) =>
              gi + 1 === position ? (
                <StrikeChip key={`${n}-${i}`} n={n} testId={`${prefix}-g${gi + 1}-${i}`} />
              ) : (
                <StrikeChip
                  key={`${n}-${i}`}
                  n={n}
                  testId={`${prefix}-g${gi + 1}-${i}`}
                  struck={strikes.isStruck(triSlot(position, gi + 1), n)}
                  onToggle={() => strikes.toggle(triSlot(position, gi + 1), n)}
                />
              )
            )}
          </div>
        </React.Fragment>
      ))}
      </div>
    </div>
  );
};

const Card = ({
  testIdPrefix,
  badge,
  headline,
  combos,
  cost,
  base,
  onArchive,
  track,
  trackSlug,
  race,
  tracks,
  onEditTarget,
  children,
}) => (
  <div
    data-testid={`card-${testIdPrefix}`}
    className="rounded-lg overflow-hidden ring-1 ring-stone-700/50"
    style={{ background: "#141414" }}
  >
    <header className="px-5 py-3 border-b border-stone-700/60 flex items-baseline justify-between gap-3 flex-wrap">
      <div className="flex items-baseline gap-3 flex-wrap">
        <span
          className="text-[10px] uppercase tracking-[0.28em] px-2 py-0.5 rounded"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            background: "#f5ecd6",
            color: "#0a0a0a",
            fontWeight: 700,
          }}
        >
          {badge}
        </span>
        <h3
          className="text-xl text-[#f5ecd6]"
          style={{ fontFamily: "'Instrument Serif', 'Times New Roman', serif", fontWeight: 400 }}
        >
          {headline}
        </h3>
        <EditableTargetBadge
          track={track}
          trackSlug={trackSlug}
          race={race}
          tracks={tracks}
          onEdit={onEditTarget}
          testId={`${testIdPrefix}-target-badge`}
        />
      </div>
      <div className="flex items-baseline gap-4 flex-wrap">
        <span
          data-testid={`card-${testIdPrefix}-count`}
          className="text-[11px] uppercase tracking-[0.2em]"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          <span className="text-stone-500">Combos: </span>
          <span className="text-[#f5ecd6] font-bold">{combos}</span>
        </span>
        <span
          data-testid={`card-${testIdPrefix}-cost`}
          className="text-[11px] uppercase tracking-[0.2em]"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
        >
          <span className="text-stone-500">Cost: </span>
          <span className="text-[#84cc16] font-bold">${cost}</span>
          <span className="text-stone-600"> @ ${base.toFixed(2)}</span>
        </span>
        {onArchive && combos > 0 && (
          <button
            data-testid={`${testIdPrefix}-archive-btn`}
            onClick={onArchive}
            className="inline-flex items-center gap-1.5 px-3 h-7 rounded-md text-[10px] font-bold tracking-[0.18em] uppercase transition-all bg-transparent text-[#84cc16] border border-[#365314] hover:bg-[#84cc16] hover:text-[#0a0a0a]"
            style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            title={`Save this ${badge} ticket to History`}
          >
            <Archive size={12} />
            Archive
          </button>
        )}
      </div>
    </header>
    {children}
  </div>
);

const TooFew = ({ minField, fieldSize, testId }) => (
  <div
    data-testid={testId}
    className="px-6 py-8 text-stone-400 text-sm leading-relaxed"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
  >
    Need at least {minField} horses with valid DD & ML to build this ticket.
    Currently: {fieldSize}.
  </div>
);

const TooLarge = ({ fieldSize, testId }) => (
  <div
    data-testid={testId}
    className="px-6 py-8 text-[#fde047] text-sm leading-relaxed"
    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
  >
    Field too large ({fieldSize} horses). Cybertote consolidates DD willpays
    into 10 betting interests for 11+ horse fields, producing duplicated
    DDPO values. No ticket generated.
  </div>
);

export const OptimalExoticsTicket = ({ leftRows, handicapped, trackName, trackSlug, race, date, leftElims, keyPost, tracks, onEditTarget }) => {
  const [exactaBase, setExactaBase] = React.useState(EXACTA_MIN_BASE);
  const [trifectaBase, setTrifectaBase] = React.useState(TRIFECTA_MIN_BASE);
  const [triWithSize, setTriWithSize] = React.useState(TRIFECTA_KB_DEFAULT_SIZE);

  const {
    fieldSize,
    tooLarge,
    front,
    lowest3DD,
    exactaAvailable,
    trifectaAvailable,
    exactaMinField,
    trifectaMinField,
    triKey,
    triWith,
    triKeyOverridden,
  } = buildExoticGroups(leftRows, leftElims, triWithSize, keyPost);

  const resetKey = `${trackSlug}|${race}`;
  const exStrikes = useTicketStrikes(resetKey);
  const triStrikes = useTicketStrikes(resetKey);
  const frontBet = exStrikes.keep("front", front);
  const backBet = exStrikes.keep("back", lowest3DD);
  // Strikes are per spot: each line's 2 "with" spots are struck independently.
  const triLines = [1, 2, 3].map((p) => {
    const groups = [1, 2, 3].map((g) => (g === p ? [triKey] : triStrikes.keep(triSlot(p, g), triWith || [])));
    const w = groups.filter((_, g) => g + 1 !== p);
    return { groups, combos: countPairs(w[0], w[1]) };
  });
  const withBet = triLines[0].groups[1];
  const triUniform = triLines.every((l, li) =>
    l.groups.every((g, gi) => gi === li || g.join() === withBet.join())
  );
  const exStruck = exStrikes.countIn("front", front) + exStrikes.countIn("back", lowest3DD);
  const triStruck = [1, 2, 3].reduce(
    (a, p) => a + [1, 2, 3].filter((g) => g !== p).reduce((b, g) => b + triStrikes.countIn(triSlot(p, g), triWith || []), 0),
    0
  );
  const exCombos = exactaAvailable ? countExactaCombos(frontBet, backBet) : 0;
  const triCombos = trifectaAvailable ? triLines.reduce((a, l) => a + l.combos, 0) : 0;
  const exactaCost = fmtCost(exCombos * exactaBase);
  const trifectaCost = fmtCost(triCombos * trifectaBase);

  const frontKey = front.join(",");
  const back3Key = lowest3DD.join(",");
  const withKey = (triWith || []).join(",");

  const archiveExacta = () => {
    if (!exactaAvailable || exCombos === 0) return;
    saveTicket({
      kind: "Exacta",
      track: trackName || "",
      track_slug: trackSlug || "",
      race: race || "",
      date: date || "",
      base: exactaBase,
      combos: exCombos,
      cost: exactaCost,
      ticket_strikes: {
        front: front.filter((n) => !frontBet.includes(n)),
        back: lowest3DD.filter((n) => !backBet.includes(n)),
      },
      horses: [
        { label: "1st Position", posts: frontBet },
        { label: "2nd Position", posts: backBet },
      ],
    });
    toast.success(
      `Exacta ticket archived — race ${race || "?"} · $${exactaCost}`,
      { duration: 3500 }
    );
  };

  const archiveTrifectaLines = () => {
    const pos = ["1st", "2nd", "3rd"];
    let saved = 0;
    let total = 0;
    triLines.forEach(({ groups, combos }, i) => {
      if (combos === 0) return;
      const cost = fmtCost(combos * trifectaBase);
      const w = [...new Set(groups.filter((_, g) => g !== i).flat())];
      saveTicket({
        kind: "Trifecta",
        shape: `key-${pos[i]}`,
        track: trackName || "",
        track_slug: trackSlug || "",
        race: race || "",
        date: date || "",
        base: trifectaBase,
        combos,
        cost,
        key: triKey,
        with_posts: w,
        ticket_strikes: Object.fromEntries(
          groups.map((g, gi) => [pos[gi], gi === i ? [] : triWith.filter((n) => !g.includes(n))])
        ),
        horses: groups.map((posts, g) => ({ label: `${pos[g]} Position`, posts })),
      });
      saved += 1;
      total += combos * trifectaBase;
    });
    toast.success(
      `Trifecta split into ${saved} key-line ticket${saved === 1 ? "" : "s"} — race ${race || "?"} · $${fmtCost(total)}`,
      { duration: 4000 }
    );
  };

  const archiveTrifecta = () => {
    if (!trifectaAvailable || triCombos === 0) return;
    if (!triUniform) {
      archiveTrifectaLines();
      return;
    }
    const allPosts = triKey != null ? [triKey, ...withBet] : withBet.slice();
    saveTicket({
      kind: "Trifecta",
      shape: "keybox",
      track: trackName || "",
      track_slug: trackSlug || "",
      race: race || "",
      date: date || "",
      base: trifectaBase,
      combos: triCombos,
      cost: trifectaCost,
      key: triKey,
      with_posts: withBet,
      ticket_strikes: { with: triWith.filter((n) => !withBet.includes(n)) },
      horses: [
        { label: "Key", posts: triKey != null ? [triKey] : [] },
        { label: "With (any 2 of)", posts: withBet },
        { label: "Any 3 of (key + with)", posts: allPosts },
      ],
    });
    toast.success(
      `Trifecta Key Box archived — race ${race || "?"} · $${trifectaCost}`,
      { duration: 3500 }
    );
  };

  if (!handicapped) return null;

  return (
    <section className="mt-8 mx-auto max-w-3xl" data-testid="optimal-exotics">
      <div className="flex items-baseline justify-between mb-4 px-1 flex-wrap gap-3">
        <h2
          className="text-2xl text-[#f5ecd6]"
          style={{
            fontFamily: "'Instrument Serif', 'Times New Roman', serif",
            fontWeight: 400,
            letterSpacing: "-0.01em",
          }}
          data-testid="exotics-title"
        >
          Optimal <em className="text-[#84cc16]">Exacta</em> &amp;{" "}
          <em className="text-[#84cc16]">Trifecta</em>{" "}
          <PanelHelp step={5} label="the Exacta & Trifecta tickets" />
        </h2>
        <span
          className="text-[11px] uppercase tracking-[0.2em] text-stone-500"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
          data-testid="exotics-field-size"
        >
          Field: {fieldSize}
          {leftElims instanceof Set && leftElims.size > 0 && (
            <span className="text-[#f87171]"> · {leftElims.size} eliminated</span>
          )}
        </span>
      </div>

      <div className="space-y-6">
        <Card
          testIdPrefix="exacta"
          badge="Exacta"
          headline="Front / Back"
          combos={exCombos}
          cost={exactaCost}
          base={exactaBase}
          onArchive={exactaAvailable ? archiveExacta : null}
          track={trackName}
          trackSlug={trackSlug}
          race={race}
          tracks={tracks}
          onEditTarget={onEditTarget}
        >
          <div className="px-5 pt-3">
            <BaseSelector
              value={exactaBase}
              onChange={setExactaBase}
              options={[1, 2, 5]}
              testIdPrefix="exacta"
            />
          </div>
          {exactaAvailable ? (
            <div className="p-5 space-y-2">
              <StrikeHint count={exStruck} onClear={exStrikes.clear} testId="exacta-strike-reset" />
              <Line
                strikes={exStrikes}
                slot="front"
                prefix="exacta-front"
                label="1st Position"
                sublabel="Top-3 IQ Power + next-low DD"
                horses={front}
                tone="lime"
              />
              <Divider label="over" testId="exacta-over" />
              <Line
                strikes={exStrikes}
                slot="back"
                prefix="exacta-back"
                label="2nd Position"
                sublabel="3 lowest DD"
                horses={lowest3DD}
                tone="cream"
              />
            </div>
          ) : tooLarge ? (
            <TooLarge fieldSize={fieldSize} testId="exacta-toolarge" />
          ) : (
            <TooFew
              minField={exactaMinField}
              fieldSize={fieldSize}
              testId="exacta-toofew"
            />
          )}
        </Card>

        <Card
          testIdPrefix="trifecta"
          badge="Trifecta"
          headline={`Key Box · ${triUniform ? `1 Key + ${withBet.length}` : "split lines"}${
            triKeyOverridden ? ` · Key locked #${triKey}` : ""
          }`}
          combos={triCombos}
          cost={trifectaCost}
          base={trifectaBase}
          onArchive={trifectaAvailable ? archiveTrifecta : null}
          track={trackName}
          trackSlug={trackSlug}
          race={race}
          tracks={tracks}
          onEditTarget={onEditTarget}
        >
          <div className="px-5 pt-3 flex items-center gap-6 flex-wrap">
            <BaseSelector
              value={trifectaBase}
              onChange={setTrifectaBase}
              options={[0.5, 1, 2]}
              testIdPrefix="trifecta"
            />
            <div
              className="flex items-center gap-2"
              data-testid="trifecta-kb-selector"
            >
              <span
                className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mr-1"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
              >
                Key + With
              </span>
              {TRIFECTA_KB_SIZES.map((v) => {
                const active = v === triWithSize;
                return (
                  <button
                    key={v}
                    data-testid={`trifecta-kb-${v}`}
                    onClick={() => setTriWithSize(v)}
                    className={`px-3 h-8 rounded-md text-[11px] font-bold tracking-[0.14em] uppercase transition-colors border ${
                      active
                        ? "bg-[#84cc16] text-[#0a0a0a] border-[#365314]"
                        : "bg-transparent text-stone-300 border-stone-700 hover:border-stone-500"
                    }`}
                    style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                    title={`1 Key + ${v}  =  ${3 * v * (v - 1)} combos`}
                  >
                    1 × {v}
                  </button>
                );
              })}
            </div>
          </div>
          {trifectaAvailable ? (
            <div className="p-5 space-y-2">
              <StrikeHint count={triStruck} onClear={triStrikes.clear} testId="trifecta-strike-reset" />
              <KeyBoxLine
                position={1}
                keyPost={triKey}
                withPosts={triWith}
                strikes={triStrikes}
                prefix="tri-k1"
                combos={triLines[0].combos}
              />
              <KeyBoxLine
                position={2}
                keyPost={triKey}
                withPosts={triWith}
                strikes={triStrikes}
                prefix="tri-k2"
                combos={triLines[1].combos}
              />
              <KeyBoxLine
                position={3}
                keyPost={triKey}
                withPosts={triWith}
                strikes={triStrikes}
                prefix="tri-k3"
                combos={triLines[2].combos}
              />
            </div>
          ) : tooLarge ? (
            <TooLarge fieldSize={fieldSize} testId="trifecta-toolarge" />
          ) : (
            <TooFew
              minField={trifectaMinField}
              fieldSize={fieldSize}
              testId="trifecta-toofew"
            />
          )}
        </Card>
      </div>
    </section>
  );
};
